import { useState, useEffect, useCallback, useRef } from 'react'
import { Outlet, NavLink, useLocation, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { useAuth } from '../../contexts/AuthContext'
import ForcedPasswordChange from '../ForcedPasswordChange'
import { useWebSocket } from '../providers/WebSocketProvider'
import { monitoringApi, notificationsApi, searchApi, activityEventsApi } from '../../services/api'
import LogoMark from '../ui/LogoMark'
import {
  CommandLineIcon,
  CheckBadgeIcon,
  BugAntIcon,
  TicketIcon,
  WrenchScrewdriverIcon,
  BookOpenIcon,
  RocketLaunchIcon,
  AdjustmentsHorizontalIcon,
  BuildingOffice2Icon,
  CloudArrowUpIcon,
  Cog6ToothIcon,
  ChartBarIcon,
  ShieldCheckIcon,
  ShieldExclamationIcon,
  BellIcon,
  Bars3Icon,
  XMarkIcon,
  ArrowRightOnRectangleIcon,
  SignalIcon,
  ServerStackIcon,
  ClipboardDocumentCheckIcon,
  DocumentMagnifyingGlassIcon,
  CubeIcon,
  CpuChipIcon,
  GlobeAltIcon,
  BoltIcon,
  PlayCircleIcon,
  CloudIcon,
  MapIcon,
} from '@heroicons/react/24/outline'

/* ------------------------------------------------------------------ */
/*  Navigation config                                                  */
/* ------------------------------------------------------------------ */
const navSections = [
  {
    label: 'Operations',
    items: [
      { name: 'Command Center', href: '/', icon: CommandLineIcon },
      { name: 'Approvals', href: '/approvals', icon: CheckBadgeIcon, badge: true },
      { name: 'Pipeline Live', href: '/pipeline/live', icon: PlayCircleIcon },
      { name: 'Vulnerabilities', href: '/vulnerabilities', icon: BugAntIcon },
      { name: 'Assets', href: '/assets', icon: CubeIcon },
      { name: 'Exceptions', href: '/exceptions', icon: ShieldExclamationIcon },
      { name: 'Tickets', href: '/tickets', icon: TicketIcon },
      { name: 'Agents', href: '/agents', icon: CpuChipIcon },
      { name: 'Activity', href: '/activity', icon: BoltIcon },
    ],
  },
  {
    label: 'Remediation',
    items: [
      { name: 'Patches', href: '/patches', icon: WrenchScrewdriverIcon },
      { name: 'Playbooks', href: '/playbooks', icon: BookOpenIcon },
      { name: 'Deployments', href: '/deployments', icon: RocketLaunchIcon },
      { name: 'Controls', href: '/compensating-controls', icon: ShieldExclamationIcon },
      { name: 'Safety', href: '/safety', icon: ShieldCheckIcon },
    ],
  },
  {
    label: 'System',
    items: [
      { name: 'Clients', href: '/clients', icon: BuildingOffice2Icon, requiresRole: 'mssp_admin' },
      { name: 'Policies', href: '/policies', icon: AdjustmentsHorizontalIcon },
      { name: 'Compliance', href: '/compliance', icon: ClipboardDocumentCheckIcon },
      { name: 'Cloud Posture', href: '/cloud-posture', icon: CloudIcon },
      { name: 'Attack Paths', href: '/attack-paths', icon: MapIcon },
      { name: 'Analytics', href: '/analytics', icon: ChartBarIcon },
      { name: 'Monitoring', href: '/monitoring', icon: SignalIcon },
      { name: 'Network Scanner', href: '/network-scanner', icon: GlobeAltIcon },
      { name: 'Connectors', href: '/connectors', icon: ServerStackIcon },
      { name: 'Audit Log', href: '/audit', icon: DocumentMagnifyingGlassIcon },
      { name: 'Scan Import', href: '/scan', icon: CloudArrowUpIcon },
      { name: 'Settings', href: '/settings', icon: Cog6ToothIcon },
    ],
  },
]

/* ------------------------------------------------------------------ */
/*  Layout Component                                                   */
/* ------------------------------------------------------------------ */
/* ------------------------------------------------------------------ */
/*  Relative time helper for notifications                             */
/* ------------------------------------------------------------------ */
function notifRelativeTime(dateStr) {
  if (!dateStr) return ''
  const diffMs = Date.now() - new Date(dateStr).getTime()
  if (diffMs < 60000) return 'just now'
  if (diffMs < 3600000) return `${Math.round(diffMs / 60000)}m ago`
  if (diffMs < 86400000) return `${Math.round(diffMs / 3600000)}h ago`
  return `${Math.round(diffMs / 86400000)}d ago`
}

/* ------------------------------------------------------------------ */
/*  Severity color dot for notifications                               */
/* ------------------------------------------------------------------ */
function severityDot(severity) {
  switch (severity) {
    case 'critical':
    case 'high':
    case 'error':
      return 'bg-red-400'
    case 'medium':
    case 'warning':
      return 'bg-amber-400'
    default:
      return 'bg-emerald-400'
  }
}

/* ------------------------------------------------------------------ */
/*  Global Search Bar                                                  */
/* ------------------------------------------------------------------ */
function SearchBar() {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState([])
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const searchRef = useRef(null)
  const navigate = useNavigate()

  useEffect(() => {
    if (query.length < 2) { setResults([]); setOpen(false); return }
    const timer = setTimeout(async () => {
      setLoading(true)
      try {
        const res = await searchApi.search(query)
        setResults(res.data.results || [])
        setOpen(true)
      } catch { setResults([]) }
      finally { setLoading(false) }
    }, 300) // debounce 300ms
    return () => clearTimeout(timer)
  }, [query])

  useEffect(() => {
    function handleClick(e) { if (searchRef.current && !searchRef.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  const typeLabels = { vulnerability: 'CVE', asset: 'HOST', ticket: 'TKT', playbook: 'PB', patch: 'PTH' }
  const typeColors = { vulnerability: 'text-signal-critical', asset: 'text-accent', ticket: 'text-signal-medium', playbook: 'text-signal-success', patch: 'text-signal-info' }

  return (
    <div className="relative flex-1 max-w-lg hidden sm:block" ref={searchRef}>
      <input
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onFocus={() => results.length > 0 && setOpen(true)}
        placeholder="Search CVEs, patches, assets... (Ctrl+K)"
        className="w-full bg-surface-0/50 border border-edge rounded-lg pl-9 pr-3 py-1.5 text-sm text-text-primary placeholder-text-muted focus:outline-none focus:border-accent/40 focus:ring-1 focus:ring-accent/20 transition-colors"
      />
      <svg className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-text-muted" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
      </svg>
      {loading && <div className="absolute right-3 top-1/2 -translate-y-1/2 h-3 w-3 border-2 border-accent border-t-transparent rounded-full animate-spin" />}

      <AnimatePresence>
        {open && results.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            className="absolute top-full mt-1 left-0 right-0 bg-surface-2 border border-edge rounded-xl shadow-2xl shadow-black/40 z-50 max-h-80 overflow-y-auto"
          >
            {results.map((r, i) => (
              <button
                key={`${r.type}-${r.id}-${i}`}
                onClick={() => { navigate(r.url); setOpen(false); setQuery('') }}
                className="flex items-center gap-3 w-full px-4 py-2.5 text-left hover:bg-surface-3 transition-colors border-b border-edge last:border-0"
              >
                <span className={`text-2xs font-bold ${typeColors[r.type] || 'text-text-tertiary'}`}>{typeLabels[r.type] || 'OTH'}</span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-text-primary truncate">{r.title}</p>
                  <p className="text-2xs text-text-tertiary truncate">{r.subtitle}</p>
                </div>
                <span className="text-2xs text-text-muted capitalize bg-surface-3 px-1.5 py-0.5 rounded">{r.type}</span>
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}


export default function Layout() {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [healthOk, setHealthOk] = useState(true)
  const location = useLocation()
  const { user, logout, mustChangePassword } = useAuth()
  const { isConnected: wsConnected } = useWebSocket()

  // Notification state
  const [notifications, setNotifications] = useState([])
  const [notifOpen, setNotifOpen] = useState(false)
  const notifRef = useRef(null)

  // Activity events unread state
  const [unreadEvents, setUnreadEvents] = useState({ count: 0, events: [] })

  const unreadCount = notifications.filter((n) => !n.read).length + unreadEvents.count

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    try {
      const res = await notificationsApi.list(20)
      const data = res.data
      setNotifications(Array.isArray(data) ? data : data.notifications || [])
    } catch {
      // silently ignore
    }
  }, [])

  // Poll notifications every 30 seconds
  useEffect(() => {
    fetchNotifications()
    const interval = setInterval(fetchNotifications, 30000)
    return () => clearInterval(interval)
  }, [fetchNotifications])

  // Fetch unread activity events every 60 seconds
  useEffect(() => {
    const fetchUnread = () => {
      activityEventsApi.getUnread().then(res => setUnreadEvents(res.data)).catch(() => {})
    }
    fetchUnread()
    const interval = setInterval(fetchUnread, 60000)
    return () => clearInterval(interval)
  }, [])

  // Mark single notification read
  const handleMarkRead = async (id) => {
    try {
      await notificationsApi.markRead(id)
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, read: true } : n))
      )
    } catch {
      // silently ignore
    }
  }

  // Mark all read
  const handleMarkAllRead = async () => {
    try {
      await notificationsApi.markAllRead()
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })))
      // Also mark activity events as read
      activityEventsApi.markRead({ all: true }).then(() => setUnreadEvents({ count: 0, events: [] })).catch(() => {})
    } catch {
      // silently ignore
    }
  }

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(e) {
      if (notifRef.current && !notifRef.current.contains(e.target)) {
        setNotifOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Periodic health check
  const checkHealth = useCallback(async () => {
    try {
      await monitoringApi.health()
      setHealthOk(true)
    } catch {
      setHealthOk(false)
    }
  }, [])

  useEffect(() => {
    checkHealth()
    const interval = setInterval(checkHealth, 30000)
    return () => clearInterval(interval)
  }, [checkHealth])

  // Close sidebar on route change (mobile)
  useEffect(() => {
    setSidebarOpen(false)
  }, [location.pathname])

  return (
    <div className="flex h-screen overflow-hidden bg-surface-1">
      {/* Forced password reset for temp-password accounts — blocks the app. */}
      {mustChangePassword && <ForcedPasswordChange />}
      {/* ---- Mobile backdrop ---- */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* ================================================================ */}
      {/*  SIDEBAR                                                         */}
      {/* ================================================================ */}
      <aside
        className={`
          fixed inset-y-0 left-0 z-50 w-60 flex flex-col
          bg-surface-2/95 backdrop-blur-md border-r border-edge
          transition-transform duration-300 ease-out
          lg:static lg:translate-x-0
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        {/* Logo */}
        <div className="flex h-14 items-center justify-between px-5 border-b border-edge shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="relative flex items-center justify-center w-8 h-8 rounded-lg bg-accent/10 border border-accent/20">
              <LogoMark className="h-6 w-6" />
            </div>
            <span className="text-base font-heading font-bold tracking-tight text-text-primary">
              Vuln<span className="text-accent">Zero</span>
            </span>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden p-1 rounded-md text-text-tertiary hover:text-text-primary transition-colors"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Navigation sections */}
        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-5">
          {navSections.map((section) => (
            <div key={section.label}>
              <p className="px-3 mb-1.5 text-2xs font-semibold uppercase tracking-widest text-text-muted">
                {section.label}
              </p>
              <div className="space-y-0.5">
                {section.items
                  /* role-gated items: also visible to superusers (the platform owner),
                     matching the backend, which bypasses require_role for superusers. */
                  .filter((item) => !item.requiresRole || user?.role === item.requiresRole || user?.is_superuser)
                  .map((item) => (
                  <NavLink
                    key={item.name}
                    to={item.href}
                    className={({ isActive }) =>
                      `group relative flex items-center gap-2.5 px-3 py-2 text-[13px] font-medium rounded-lg transition-all duration-150 ${
                        isActive
                          ? 'bg-accent/10 text-accent border border-accent/15'
                          : 'text-text-secondary hover:text-text-primary hover:bg-surface-3/60 border border-transparent'
                      }`
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <item.icon
                          className={`h-[18px] w-[18px] shrink-0 transition-colors duration-150 ${
                            isActive ? 'text-accent' : 'text-text-tertiary group-hover:text-text-secondary'
                          }`}
                        />
                        <span className="truncate">{item.name}</span>
                        {isActive && (
                          <motion.div
                            layoutId="nav-active-dot"
                            className="absolute right-2.5 h-1.5 w-1.5 rounded-full bg-accent shadow-glow-sm"
                            transition={{ type: 'spring', stiffness: 350, damping: 30 }}
                          />
                        )}
                      </>
                    )}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>

        {/* Status footer */}
        <div className="shrink-0 px-3 py-3 border-t border-edge">
          <div className="flex items-center justify-between px-3 py-2 rounded-lg bg-surface-0/40">
            <div className="flex items-center gap-2">
              <div
                className={`h-1.5 w-1.5 rounded-full ${
                  healthOk ? 'bg-signal-success animate-beacon' : 'bg-signal-danger'
                }`}
              />
              <span className="text-2xs font-medium text-text-tertiary">
                {healthOk ? 'System Healthy' : 'System Degraded'}
              </span>
            </div>
          </div>
        </div>
      </aside>

      {/* ================================================================ */}
      {/*  MAIN CONTENT                                                    */}
      {/* ================================================================ */}
      <div className="flex flex-1 flex-col overflow-hidden min-w-0">
        {/* Header */}
        <header className="flex h-14 items-center justify-between px-4 sm:px-6 border-b border-edge bg-surface-2/60 backdrop-blur-md shrink-0 z-10">
          {/* Left: mobile menu + search */}
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <button
              onClick={() => setSidebarOpen(true)}
              aria-label="Open navigation menu"
              className="lg:hidden p-1.5 rounded-lg text-text-tertiary hover:text-text-primary hover:bg-surface-3 transition-colors"
            >
              <Bars3Icon className="h-5 w-5" />
            </button>

            <SearchBar />
          </div>

          {/* Right: notifications + user */}
          <div className="flex items-center gap-2">
            {/* WebSocket connection indicator — the only way operators
                can tell whether the UI is showing live data or a stale
                snapshot. Pulses when connected, fades red when not.   */}
            <div
              className={`hidden sm:flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-2xs font-medium ${
                wsConnected
                  ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300'
                  : 'border-red-500/40 bg-red-500/10 text-red-300'
              }`}
              title={
                wsConnected
                  ? 'Real-time stream connected — dashboards update on every event'
                  : 'Real-time stream disconnected — data may be stale'
              }
            >
              <span
                className={`h-1.5 w-1.5 rounded-full ${
                  wsConnected ? 'bg-emerald-400 animate-pulse' : 'bg-red-400'
                }`}
              />
              {wsConnected ? 'LIVE' : 'OFFLINE'}
            </div>

            {/* Notification bell dropdown */}
            <div className="relative" ref={notifRef}>
              <button
                onClick={() => setNotifOpen((prev) => !prev)}
                aria-label={unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications'}
                aria-expanded={notifOpen}
                className="relative p-2 rounded-lg text-text-tertiary hover:text-text-primary hover:bg-surface-3 transition-colors"
              >
                <BellIcon className="h-5 w-5" />
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center h-4 min-w-[16px] px-1 rounded-full bg-signal-danger text-white text-2xs font-bold">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </button>

              <AnimatePresence>
                {notifOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -8, scale: 0.96 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -8, scale: 0.96 }}
                    transition={{ duration: 0.15, ease: 'easeOut' }}
                    className="absolute right-0 top-full mt-2 w-80 sm:w-96 rounded-xl bg-surface-2 border border-edge shadow-2xl shadow-black/40 z-50 overflow-hidden"
                  >
                    {/* Dropdown header */}
                    <div className="flex items-center justify-between px-4 py-3 border-b border-edge">
                      <h3 className="text-sm font-semibold text-text-primary">Notifications</h3>
                      {unreadCount > 0 && (
                        <button
                          onClick={handleMarkAllRead}
                          className="text-2xs font-medium text-accent hover:text-accent/80 transition-colors"
                        >
                          Mark all read
                        </button>
                      )}
                    </div>

                    {/* Notification list */}
                    <div className="max-h-[400px] overflow-y-auto divide-y divide-edge/50">
                      {notifications.length === 0 && unreadEvents.events.length === 0 ? (
                        <div className="px-4 py-8 text-center">
                          <BellIcon className="h-8 w-8 text-text-muted mx-auto mb-2" />
                          <p className="text-sm text-text-tertiary">No notifications</p>
                        </div>
                      ) : (
                        <>
                          {notifications.map((notif) => (
                            <button
                              key={notif.id}
                              onClick={() => handleMarkRead(notif.id)}
                              className={`w-full text-left px-4 py-3 hover:bg-surface-3/60 transition-colors ${
                                !notif.read ? 'bg-accent/5' : ''
                              }`}
                            >
                              <div className="flex items-start gap-3">
                                <div className={`mt-1.5 h-2 w-2 rounded-full shrink-0 ${severityDot(notif.severity)}`} />
                                <div className="flex-1 min-w-0">
                                  <p className={`text-xs font-medium truncate ${
                                    !notif.read ? 'text-text-primary' : 'text-text-secondary'
                                  }`}>
                                    {notif.title}
                                  </p>
                                  <p className="text-2xs text-text-tertiary mt-0.5 line-clamp-2">
                                    {notif.message}
                                  </p>
                                  <p className="text-2xs text-text-muted mt-1">
                                    {notifRelativeTime(notif.created_at)}
                                  </p>
                                </div>
                                {!notif.read && (
                                  <div className="h-1.5 w-1.5 rounded-full bg-accent shrink-0 mt-1.5" />
                                )}
                              </div>
                            </button>
                          ))}
                          {/* Activity events in notification dropdown */}
                          {unreadEvents.events.slice(0, 5).map((evt) => (
                            <div key={`evt-${evt.id}`} className="px-4 py-2.5 border-b border-edge/30 hover:bg-surface-3/40 transition-colors">
                              <div className="flex items-center gap-2">
                                <span className={`h-2 w-2 rounded-full shrink-0 ${evt.severity === 'critical' ? 'bg-red-400' : 'bg-amber-400'}`} />
                                <p className="text-xs text-text-primary truncate flex-1">{evt.title}</p>
                              </div>
                              <p className="text-2xs text-text-muted mt-0.5 pl-4">{evt.timestamp ? new Date(evt.timestamp).toLocaleString() : ''}</p>
                            </div>
                          ))}
                        </>
                      )}
                    </div>
                    <a href="/activity" className="block text-center py-2.5 text-xs text-accent hover:bg-surface-3/40 border-t border-edge transition-colors">
                      View all activity &rarr;
                    </a>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <div className="h-6 w-px bg-edge mx-1" />

            <div className="flex items-center gap-2.5">
              <div className="flex items-center justify-center h-7 w-7 rounded-full bg-accent/10 border border-accent/20 text-accent text-xs font-bold">
                {(user?.username || 'A')[0].toUpperCase()}
              </div>
              <div className="hidden sm:block">
                <p className="text-xs font-medium text-text-primary leading-none">{user?.full_name || user?.username || 'Admin'}</p>
                <p className="text-2xs text-text-tertiary mt-0.5">{user?.role || 'admin'}</p>
              </div>
              <button
                onClick={logout}
                className="p-1.5 rounded-lg text-text-muted hover:text-signal-danger hover:bg-signal-danger/10 transition-colors ml-1"
                title="Sign out"
              >
                <ArrowRightOnRectangleIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto">
          <div className="mx-auto max-w-[1440px] px-4 sm:px-6 py-5">
            <AnimatePresence mode="wait">
              <motion.div
                key={location.pathname}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15, ease: 'easeOut' }}
              >
                <Outlet />
              </motion.div>
            </AnimatePresence>
          </div>
        </main>
      </div>
    </div>
  )
}
